import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    LinearScale,
    Tooltip,
} from "chart.js";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const buildNumberFormatter = () =>
    new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
    });

export const registerWarehouseTrendChart = (Alpine) => {
    Alpine.data(
        "warehouseTrendChart",
        ({ label = "", points = [], selectedActorName = null } = {}) => ({
            chart: null,
            numberFormatter: buildNumberFormatter(),
            init() {
                if (!this.$refs.canvas || points.length === 0) {
                    return;
                }

                this.chart = new Chart(this.$refs.canvas, {
                    type: "bar",
                    data: {
                        labels: points.map((point) => point.label),
                        datasets: this.datasets(label, points, selectedActorName),
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                            mode: "index",
                            intersect: false,
                        },
                        plugins: {
                            legend: {
                                display: selectedActorName !== null,
                                position: "bottom",
                                labels: {
                                    usePointStyle: true,
                                    boxWidth: 8,
                                },
                            },
                            tooltip: {
                                callbacks: {
                                    title: (items) => {
                                        const index = items[0]?.dataIndex ?? 0;

                                        return points[index]?.period ?? "";
                                    },
                                    label: (context) => {
                                        const point = points[context.dataIndex] ?? {};
                                        const display = this.tooltipDisplay(
                                            point,
                                            context.dataset.key,
                                            context.raw,
                                        );

                                        return `${context.dataset.label}: ${display}`;
                                    },
                                },
                            },
                        },
                        scales: {
                            x: {
                                stacked: selectedActorName !== null,
                                grid: { display: false },
                                border: { display: false },
                                ticks: {
                                    color: "rgb(107, 114, 128)",
                                    maxRotation: 0,
                                    autoSkip: true,
                                    maxTicksLimit: 4,
                                },
                            },
                            y: {
                                beginAtZero: true,
                                stacked: selectedActorName !== null,
                                border: { display: false },
                                ticks: {
                                    color: "rgb(107, 114, 128)",
                                    precision: 0,
                                    callback: (value) =>
                                        this.numberFormatter.format(Number(value)),
                                },
                                grid: {
                                    color: "rgba(148, 163, 184, 0.16)",
                                },
                            },
                        },
                    },
                });
            },
            datasets(label, points, selectedActorName) {
                const squareRadius = {
                    topLeft: 0,
                    topRight: 0,
                };
                const topRadius = {
                    topLeft: 3,
                    topRight: 3,
                };

                if (selectedActorName === null) {
                    return [
                        {
                            key: "value",
                            label,
                            data: points.map((point) => Number(point.value ?? 0)),
                            backgroundColor: points.map((point) =>
                                point.empty
                                    ? "rgba(229, 231, 235, 0.9)"
                                    : "rgba(37, 99, 235, 0.72)",
                            ),
                            hoverBackgroundColor: points.map((point) =>
                                point.empty
                                    ? "rgba(209, 213, 219, 0.95)"
                                    : "rgba(29, 78, 216, 0.9)",
                            ),
                            borderRadius: {
                                topLeft: 3,
                                topRight: 3,
                            },
                            barPercentage: 0.78,
                            categoryPercentage: 0.82,
                            maxBarThickness: 18,
                        },
                    ];
                }

                return [
                    {
                        key: "actor",
                        label: selectedActorName,
                        data: points.map((point) => Number(point.actorValue ?? 0)),
                        backgroundColor: "rgba(37, 99, 235, 0.78)",
                        hoverBackgroundColor: "rgba(29, 78, 216, 0.92)",
                        borderRadius: (context) =>
                            Number(points[context.dataIndex]?.restValue ?? 0) > 0
                                ? squareRadius
                                : topRadius,
                        barPercentage: 0.72,
                        categoryPercentage: 0.78,
                        maxBarThickness: 18,
                        stack: "team",
                    },
                    {
                        key: "rest",
                        label: "Rest of team",
                        data: points.map((point) => Number(point.restValue ?? 0)),
                        backgroundColor: "rgba(148, 163, 184, 0.22)",
                        hoverBackgroundColor: "rgba(148, 163, 184, 0.35)",
                        borderRadius: topRadius,
                        barPercentage: 0.9,
                        categoryPercentage: 0.78,
                        maxBarThickness: 18,
                        stack: "team",
                    },
                ];
            },
            tooltipDisplay(point, key, raw) {
                if (key === "actor") {
                    return point.actorDisplay ?? this.numberFormatter.format(Number(raw ?? 0));
                }

                if (key === "rest") {
                    return point.restDisplay ?? this.numberFormatter.format(Number(raw ?? 0));
                }

                return point.display ?? this.numberFormatter.format(Number(raw ?? 0));
            },
            destroy() {
                if (this.chart) {
                    this.chart.destroy();
                    this.chart = null;
                }
            },
        }),
    );
};
